import { ThemeEvents } from '@theme/events';

/**
 * Checkout gate for Premade.
 *
 * Keeps the Check Out button disabled until the cart meets the minimum meal
 * count AND (when postcode validation is on) the customer's postcode is
 * confirmed as deliverable.
 *
 * Replaces an inline script that, on every cart change, fired a redundant
 * `/cart.js` fetch and ran a whole-drawer MutationObserver. This version reads
 * the cart count straight off the re-rendered DOM and re-applies on the frame
 * after the theme's `cart:update` (the section morph is synchronous, so a
 * requestAnimationFrame lands just after it) — no extra network, no observer.
 *
 * Runs once (ES module) and drives every cart surface on the page (drawer and
 * /cart), so it also works when both are present.
 */

const CONFIG = readConfig();

// If there are no blocked postcodes, there's nothing to confirm.
let zipConfirmed = CONFIG.blockedZips.length === 0;

function readConfig() {
  const el = document.querySelector('[data-cart-gate]');
  return {
    minItems: parseInt(el?.dataset.minItems ?? '6', 10) || 6,
    blockedZips: (el?.dataset.blockedZips ?? '')
      .split(/[\n,]+/)
      .map((code) => code.trim())
      .filter(Boolean),
    successMsg: el?.dataset.successMsg || 'We deliver to your area!',
    errorMsg: el?.dataset.errorMsg || "Sorry, we don't deliver to your area yet.",
  };
}

/**
 * The current cart item count, read from the config element which cart-summary
 * re-renders (with the fresh `cart.item_count`) on every morph.
 * @returns {number}
 */
function getCount() {
  const el = document.querySelector('[data-cart-gate]');
  return parseInt(el?.dataset.cartCount ?? '0', 10) || 0;
}

/**
 * Apply the gate state to every checkout bar on the page.
 */
function apply() {
  const count = getCount();
  const enoughMeals = count >= CONFIG.minItems;
  const canCheckout = enoughMeals && zipConfirmed;

  for (const container of document.querySelectorAll('.cart__total-container')) {
    const button = container.querySelector('[name="checkout"]');
    const alert = container.querySelector('#cart-gate-alert, .cart-validation-error');

    if (button instanceof HTMLButtonElement) button.disabled = !canCheckout;
    container.classList.toggle('disabled', !canCheckout);

    if (!(alert instanceof HTMLElement)) continue;

    if (!enoughMeals) {
      alert.textContent = `⚠️ Please add at least ${CONFIG.minItems} meals to continue.`;
      alert.style.display = 'block';
    } else if (!zipConfirmed) {
      alert.textContent = '⚠️ Please confirm your postcode before checkout.';
      alert.style.display = 'block';
    } else {
      alert.style.display = 'none';
    }
  }
}

/**
 * Handle a postcode "Check" click. Delegated so it survives section morphs.
 * @param {MouseEvent} event
 */
function onCheckClick(event) {
  const target = /** @type {Element} */ (event.target);
  if (!target.closest?.('#check-zip-code')) return;

  const zone = target.closest('.cart__summary-zipcode') ?? document;
  const input = /** @type {HTMLInputElement | null} */ (zone.querySelector('#zip-code-input'));
  const message = /** @type {HTMLElement | null} */ (zone.querySelector('#zip-code-message'));

  const entered = (input?.value ?? '').trim();
  zipConfirmed = !CONFIG.blockedZips.includes(entered);

  if (message) {
    message.textContent = zipConfirmed ? CONFIG.successMsg : CONFIG.errorMsg;
    message.style.color = zipConfirmed ? 'var(--color-success, green)' : 'var(--color-error, red)';
    message.style.display = 'block';
  }

  // Dismiss the iOS keyboard cleanly (its lingering focus can strand the modal).
  input?.blur();
  apply();
}

/**
 * A change to the postcode field invalidates a prior confirmation.
 * @param {Event} event
 */
function onZipInput(event) {
  const target = /** @type {Element} */ (event.target);
  if (!target.matches?.('#zip-code-input')) return;

  zipConfirmed = false;
  const zone = target.closest('.cart__summary-zipcode') ?? document;
  const message = /** @type {HTMLElement | null} */ (zone.querySelector('#zip-code-message'));
  if (message) message.style.display = 'none';
  apply();
}

// The section morph runs synchronously right after `cart:update` dispatches,
// so re-apply on the next frame to land just after the DOM is replaced.
document.addEventListener(ThemeEvents.cartUpdate, () => requestAnimationFrame(apply));

document.addEventListener('click', onCheckClick);
document.addEventListener('input', onZipInput);

apply();
